import { Loader2, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { errorMessage } from '../lib/api';
import { humanize, toneOf, type Tone } from '../lib/format';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

// ───────── Buttons ─────────
type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-forest-900 text-cream hover:bg-forest-800 disabled:bg-forest-900/50',
  secondary: 'bg-cream-surface text-forest-900 border border-cream-border hover:bg-forest-50 disabled:opacity-50',
  danger: 'bg-danger text-white hover:opacity-90 disabled:opacity-50',
  ghost: 'text-forest-900 hover:bg-forest-50 disabled:opacity-50',
};
export function Button({ variant = 'primary', loading, className, children, disabled, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean }) {
  return (
    <button {...rest} disabled={disabled || loading} className={cx('inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-600 focus-visible:ring-offset-1 disabled:cursor-not-allowed', VARIANTS[variant], className)}>
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}

// ───────── Form fields ─────────
const inputCls = 'w-full rounded-md border border-cream-border bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-muted/70 focus:border-forest-600 focus:outline-none focus:ring-2 focus:ring-forest-600/20 disabled:bg-cream';
export function Field({ label, error, hint, children, id }: { label: string; error?: string; hint?: string; children: (id: string, describedBy?: string) => ReactNode; id?: string }) {
  const auto = useId();
  const fid = id ?? auto;
  const desc = error ? `${fid}-err` : hint ? `${fid}-hint` : undefined;
  return (
    <div className="space-y-1">
      <label htmlFor={fid} className="block text-sm font-medium text-ink">{label}</label>
      {children(fid, desc)}
      {error ? <p id={`${fid}-err`} className="text-xs text-danger">{error}</p> : hint ? <p id={`${fid}-hint`} className="text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}
export function Input({ label, error, hint, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string; hint?: string }) {
  return <Field label={label} error={error} hint={hint} id={rest.id}>{(id, d) => <input id={id} aria-invalid={!!error} aria-describedby={d} className={inputCls} {...rest} />}</Field>;
}
export function Textarea({ label, error, hint, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; error?: string; hint?: string }) {
  return <Field label={label} error={error} hint={hint}>{(id, d) => <textarea id={id} aria-invalid={!!error} aria-describedby={d} className={cx(inputCls, 'min-h-[88px]')} {...rest} />}</Field>;
}
export function Select({ label, error, hint, options, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label: string; error?: string; hint?: string; options: { value: string; label: string }[] }) {
  return (
    <Field label={label} error={error} hint={hint}>
      {(id, d) => <select id={id} aria-invalid={!!error} aria-describedby={d} className={inputCls} {...rest}>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>}
    </Field>
  );
}
export function Checkbox({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
      <input type="checkbox" className="h-4 w-4 rounded border-cream-border accent-forest-900" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
/** Compact filter controls for list toolbars. */
export function FilterSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <label className="text-sm">
      <span className="sr-only">{label}</span>
      <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className={cx(inputCls, 'w-auto')}>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
    </label>
  );
}
export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  useEffect(() => {
    const t = setTimeout(() => v !== value && onChange(v), 350);
    return () => clearTimeout(t);
  }, [v, value, onChange]);
  return <input type="search" aria-label={placeholder} placeholder={placeholder} value={v} onChange={(e) => setV(e.target.value)} className={cx(inputCls, 'max-w-xs')} />;
}

// ───────── Display ─────────
const TONE_CLS: Record<Tone, string> = { green: 'bg-forest-100 text-forest-800', gold: 'bg-gold-100 text-gold-700', red: 'bg-red-50 text-danger', blue: 'bg-blue-50 text-info', grey: 'bg-stone-100 text-ink-muted' };
export function Badge({ status, children, tone }: { status?: string; children?: ReactNode; tone?: Tone }) {
  return <span className={cx('inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold', TONE_CLS[tone ?? toneOf(status ?? '')])}>{children ?? humanize(status ?? '')}</span>;
}
export function Card({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx('rounded-lg border border-cream-border bg-cream-surface p-4 shadow-sm', className)}>
      {(title || actions) && <div className="mb-3 flex flex-wrap items-center justify-between gap-2">{title && <h2 className="font-heading text-xl font-semibold text-forest-900">{title}</h2>}{actions}</div>}
      {children}
    </section>
  );
}
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest-900">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-ink-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <div role="status" className="flex items-center gap-2 p-6 text-sm text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />{label}…</div>;
}
export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-danger">
      <span>{errorMessage(error)}</span>
      {onRetry && <Button variant="secondary" onClick={onRetry}>Try again</Button>}
    </div>
  );
}
export function Empty({ children }: { children: ReactNode }) {
  return <div className="p-8 text-center text-sm text-ink-muted">{children}</div>;
}
export function DL({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
      {items.map(([k, v], i) => <div key={i} className="contents"><dt className="text-ink-muted">{k}</dt><dd className="break-words">{v ?? '—'}</dd></div>)}
    </dl>
  );
}

// ───────── Table ─────────
export interface Column<T> { header: string; cell: (row: T) => ReactNode; className?: string }
export function Table<T>({ rows, columns, rowKey, onRowClick, empty = 'Nothing here yet.' }: { rows: T[]; columns: Column<T>[]; rowKey: (r: T) => string; onRowClick?: (r: T) => void; empty?: ReactNode }) {
  if (!rows.length) return <Empty>{empty}</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead><tr className="border-b border-cream-border text-xs uppercase tracking-wide text-ink-muted">{columns.map((c) => <th key={c.header} scope="col" className={cx('px-3 py-2 font-semibold', c.className)}>{c.header}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)} onClick={onRowClick ? () => onRowClick(r) : undefined} onKeyDown={onRowClick ? (e) => e.key === 'Enter' && onRowClick(r) : undefined} tabIndex={onRowClick ? 0 : undefined}
              className={cx('border-b border-cream-border/60 last:border-0', onRowClick && 'cursor-pointer hover:bg-forest-50 focus:bg-forest-50 focus:outline-none')}>
              {columns.map((c) => <td key={c.header} className={cx('px-3 py-2.5 align-top', c.className)}>{c.cell(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function Pagination({ page, totalPages, total, onPage }: { page: number; totalPages: number; total: number; onPage: (p: number) => void }) {
  if (totalPages <= 1) return <p className="mt-3 text-xs text-ink-muted">{total} result{total === 1 ? '' : 's'}</p>;
  return (
    <nav aria-label="Pagination" className="mt-3 flex items-center justify-between text-sm">
      <span className="text-ink-muted">Page {page} of {totalPages} · {total} results</span>
      <div className="flex gap-2">
        <Button variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</Button>
        <Button variant="secondary" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Next</Button>
      </div>
    </nav>
  );
}

// ───────── Dialog ─────────
export function Dialog({ open, title, onClose, children, footer, wide }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button:not([data-close])');
    (first ?? ref.current)?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); prev?.focus(); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className={cx('max-h-[92vh] w-full overflow-y-auto rounded-t-xl bg-cream-surface p-5 shadow-xl sm:rounded-xl', wide ? 'sm:max-w-3xl' : 'sm:max-w-lg')}>
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={titleId} className="font-heading text-2xl font-semibold text-forest-900">{title}</h2>
          <button data-close aria-label="Close" onClick={onClose} className="rounded p-1 text-ink-muted hover:bg-forest-50"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4">{children}</div>
        {footer && <div className="mt-5 flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

// ───────── Toasts ─────────
interface Toast { id: number; kind: 'success' | 'error'; text: string }
const ToastCtx = createContext<{ success: (t: string) => void; error: (e: unknown) => void } | null>(null);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((kind: Toast['kind'], text: string) => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, kind, text }].slice(-3)); // keep the newest three on screen
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), kind === 'error' ? 7000 : 3500);
  }, []);
  const api = useCallback(() => ({ success: (t: string) => push('success', t), error: (e: unknown) => push('error', typeof e === 'string' ? e : errorMessage(e)) }), [push]);
  const [value] = useState(api);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(92vw,380px)] flex-col gap-2">
        {items.map((t) => (
          <div key={t.id} role={t.kind === 'error' ? 'alert' : 'status'} className={cx('pointer-events-auto rounded-md px-4 py-3 text-sm shadow-lg', t.kind === 'success' ? 'bg-forest-900 text-cream' : 'bg-danger text-white')}>{t.text}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export function useToast() {
  const v = useContext(ToastCtx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}

export { cx };
